import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { RiDeleteBin6Line } from "react-icons/ri"
import { FiUploadCloud, FiFileText } from "react-icons/fi"

import IconBtn from "../../../common/IconBtn"
import ConfirmationModal from "../../../common/ConfirmationModal"
import {
  getCourseMaterials,
  addMaterial,
  deleteMaterial,
} from "../../../../services/operations/materialAPI"

const formatSize = (bytes) => {
  if (!bytes) return ""
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

//Notes and other downloads for a course the instructor owns.
const MaterialsManager = ({ courseId }) => {
  const { token } = useSelector((state) => state.auth)

  const [materials, setMaterials] = useState([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [confirmationModal, setConfirmationModal] = useState(null)

  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [file, setFile] = useState(null)

  const load = async () => {
    setLoading(true)
    //never let an undefined result reach state...the render reads .length off it
    setMaterials((await getCourseMaterials(courseId, token)) ?? [])
    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId])

  const handleUpload = async (event) => {
    event.preventDefault()

    if (!title.trim() || !file) return

    //multipart, since this carries a file
    const formData = new FormData()
    formData.append("courseId", courseId)
    formData.append("title", title.trim())
    formData.append("description", description.trim())
    formData.append("file", file)

    setUploading(true)
    const ok = await addMaterial(formData, token)
    setUploading(false)

    if (ok) {
      setTitle("")
      setDescription("")
      setFile(null)
      //the file input keeps its own value, so it needs clearing directly
      if (event.target.reset) event.target.reset()
      load()
    }
  }

  const handleDelete = async (material) => {
    const ok = await deleteMaterial(courseId, material._id, token)
    if (ok) load()
    setConfirmationModal(null)
  }

  return (
    <div className="rounded-md border-[1px] border-richblack-700 bg-richblack-800 p-6">
      <h2 className="text-xl font-semibold text-richblack-5">Course materials</h2>
      <p className="mt-1 text-sm text-richblack-300">
        Notes and files your enrolled students can download.
      </p>

      <form onSubmit={handleUpload} className="mt-6 flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-richblack-5">
            Title <sup className="text-pink-200">*</sup>
          </span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Week 1 notes"
            className="rounded-md bg-richblack-700 p-3 text-richblack-5"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm text-richblack-5">Description</span>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
            className="rounded-md bg-richblack-700 p-3 text-richblack-5"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm text-richblack-5">
            File <sup className="text-pink-200">*</sup>
          </span>
          {/* cursor-pointer on both the field and the Choose file button, which
          is a separate pseudo element and does not inherit it */}
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="cursor-pointer rounded-md bg-richblack-700 p-3 text-richblack-200 file:mr-4 file:cursor-pointer file:rounded file:border-0 file:bg-yellow-50 file:px-3 file:py-1 file:font-semibold file:text-richblack-900 hover:file:bg-yellow-100"
          />
        </label>

        <div>
          <IconBtn
            type="submit"
            disabled={uploading || !title.trim() || !file}
            text={uploading ? "Uploading..." : "Upload material"}
          >
            <FiUploadCloud />
          </IconBtn>
        </div>
      </form>

      <div className="mt-8">
        {loading ? (
          <div className="spinner"></div>
        ) : materials.length === 0 ? (
          <p className="text-richblack-300">Nothing uploaded yet.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {materials.map((material) => (
              <div
                key={material._id}
                className="flex items-center justify-between gap-4 rounded-md border-[1px] border-richblack-700 bg-richblack-900 p-4"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <FiFileText className="shrink-0 text-xl text-yellow-50" />
                  <div className="min-w-0">
                    <p className="truncate font-medium text-richblack-5">
                      {material.title}
                    </p>
                    {material.description && (
                      <p className="truncate text-sm text-richblack-300">
                        {material.description}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-richblack-400">
                      {material.originalName} {formatSize(material.bytes)}
                    </p>
                  </div>
                </div>

                <button
                  title="Delete"
                  onClick={() =>
                    setConfirmationModal({
                      text1: "Delete this material?",
                      text2: "The file is removed from storage as well.",
                      btn1Text: "Delete",
                      btn2Text: "Cancel",
                      btn1Handler: () => handleDelete(material),
                      btn2Handler: () => setConfirmationModal(null),
                    })
                  }
                  className="shrink-0 text-lg text-pink-200"
                >
                  <RiDeleteBin6Line />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {confirmationModal && <ConfirmationModal modalData={confirmationModal} />}
    </div>
  )
}

export default MaterialsManager
